import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaPlus, FaFilm, FaParagraph } from 'react-icons/fa';

/**
 * Plus control for timeline rows — hover/focus reveals segment type suggestions.
 */
export default function SegmentAddMenu({
  suggestions = [],
  onAddBlank,
  onAddClipTag,
  onAddGenericClip,
  showBlankOption = true,
  showGenericClipOption = false,
  blankLabel = 'Blank segment',
  genericClipLabel = 'Blank clip segment',
  title = 'Add segment after',
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const closeTimer = useRef(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setOpen(false), 160);
  }, [clearCloseTimer]);

  const openMenu = useCallback(() => {
    clearCloseTimer();
    setOpen(true);
  }, [clearCloseTimer]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  const hasMenu = showBlankOption || showGenericClipOption || suggestions.length > 0;

  const handleBlank = () => {
    onAddBlank?.();
    setOpen(false);
  };

  const handleClipTag = (tag) => {
    onAddClipTag?.(tag);
    setOpen(false);
  };

  const handleGenericClip = () => {
    onAddGenericClip?.();
    setOpen(false);
  };

  if (!hasMenu) {
    return (
      <div
        className="control-button"
        onClick={onAddBlank}
        title={title}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onAddBlank?.();
          }
        }}
      >
        <FaPlus className="icon add-icon" />
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className={`segment-add-menu ${open ? 'segment-add-menu--open' : ''}`}
      onMouseEnter={openMenu}
      onMouseLeave={scheduleClose}
      onFocus={openMenu}
      onBlur={scheduleClose}
    >
      <div
        className="control-button segment-add-menu__trigger"
        title={title}
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(v => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(v => !v);
          }
        }}
      >
        <FaPlus className="icon add-icon" />
      </div>

      {open && (
        <div className="segment-add-menu__pop" role="menu">
          {showBlankOption && (
            <button type="button" className="segment-add-menu__item" role="menuitem" onClick={handleBlank}>
              <FaParagraph aria-hidden="true" />
              <span>{blankLabel}</span>
            </button>
          )}

          {suggestions.length > 0 && (
            <>
              {(showBlankOption || showGenericClipOption) && (
                <div className="segment-add-menu__divider" role="separator" />
              )}
              <p className="segment-add-menu__heading">Clip segments (tagged in Videos)</p>
              {suggestions.map(s => (
                <button
                  key={s.tag}
                  type="button"
                  className="segment-add-menu__item segment-add-menu__item--clip"
                  role="menuitem"
                  onClick={() => handleClipTag(s.tag)}
                  title={`Insert ${s.clipText} with tag "${s.tag}"`}
                >
                  <FaFilm aria-hidden="true" />
                  <span className="segment-add-menu__item-main">
                    <strong>{s.label}</strong>
                    <code>{s.clipText}</code>
                  </span>
                </button>
              ))}
            </>
          )}

          {showGenericClipOption && (
            <>
              <div className="segment-add-menu__divider" role="separator" />
              <button
                type="button"
                className="segment-add-menu__item"
                role="menuitem"
                onClick={handleGenericClip}
              >
                <FaFilm aria-hidden="true" />
                <span>{genericClipLabel}</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
